import React, { useEffect, useState } from "react";
import { Form, Row, Col, Alert } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useDispatch, useSelector } from "react-redux";
import { ReduxState } from "../../../../../redux";
import { LoadingTrackedButton } from "../../../../Docuclipper/TrackedButton";
import { downloadSpreadsheetHelper } from "./utils";
import { convertExternalFiltersToTableFilterConfig } from "src/utils/filters";
import PopoverHelp from "../../../../Docuclipper/PopoverHelp";
import {
  getQbdAccounts,
  QbdAccount,
  getIifPreflight,
} from "../../../../../docuclipper/api";
import {
  trackInvoiceExportAttempted,
  trackInvoiceExportSucceeded,
  trackInvoiceExportFailed,
} from "src/utils/invoiceExportAnalytics";

type Props = {
  /**
   * "modal" reads selected docs from state.InvoiceManager (DownloadInvoiceModal).
   * Default reads from state.TransactionManager (in-page DownloadSection).
   */
  source?: "modal" | "page";
  onExportSucceeded?: () => void;
};

const KB_INVOICE_IIF =
  "/docs/export-invoices-to-quickbooks-desktop-iif/";

export const InvoiceIifOptions: React.FC<Props> = ({ source = "page", onExportSucceeded }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [iifTxnType, setIifTxnType] = useState<"BILL" | "INVOICE">("BILL");

  const { job } = useSelector((state: ReduxState) => state.JobData);
  const pageDownloadOptions = useSelector(
    (state: ReduxState) => state.TransactionManager.downloadOptions
  );
  const modalDownloadOptions = useSelector(
    (state: ReduxState) => state.InvoiceManager.downloadOptions
  );
  const { selectedDocumentIds, selectedFieldNames } =
    source === "modal" ? modalDownloadOptions : pageDownloadOptions;
  const { selectedL1: l1, selectedL2: l2, tagLevels } = useSelector(
    (state: ReduxState) => state.TagLevel
  );
  const { externalFilterSortBy, externalFilterSortDirection, externalFilters } =
    useSelector((state: ReduxState) => state.ExternalFilter);
  const { options } = useSelector((state: ReduxState) => state.DownloadOptions);

  // Resolve the project's numeric tagLevelId from the tree (L1=folder, L2=project).
  const projectTagLevelId: number | null =
    (l1 &&
      l2 &&
      tagLevels?.root?.[l1]?.[l2]?.metadata?.tagId &&
      Number(tagLevels.root[l1][l2].metadata.tagId)) ||
    null;

  const [coa, setCoa] = useState<QbdAccount[] | null>(null);
  useEffect(() => {
    if (!projectTagLevelId) {
      setCoa(null);
      return;
    }
    let cancelled = false;
    getQbdAccounts(projectTagLevelId)
      .then((res) => {
        if (cancelled) return;
        setCoa(res?.accounts || []);
      })
      .catch(() => {
        if (cancelled) return;
        setCoa([]);
      });
    return () => {
      cancelled = true;
    };
  }, [projectTagLevelId]);

  const projectSettingsHref =
    l1 && l2
      ? `/app/extractdata?l1=${encodeURIComponent(l1)}&l2=${encodeURIComponent(
          l2
        )}&openProjectSettings=quickbooks`
      : "/app/extractdata";

  const hasCoa = coa !== null && coa.length > 0;

  const [unmatchedCategories, setUnmatchedCategories] = useState<string[]>([]);
  const docIdsKey = (selectedDocumentIds || []).join(",");
  useEffect(() => {
    if (!projectTagLevelId || !hasCoa || !selectedDocumentIds || selectedDocumentIds.length === 0) {
      setUnmatchedCategories([]);
      return;
    }
    let cancelled = false;
    getIifPreflight(projectTagLevelId, selectedDocumentIds, "invoiceMode", iifTxnType)
      .then(res => {
        if (cancelled) return;
        setUnmatchedCategories(res?.unmatchedAccountNames || []);
      })
      .catch(() => {
        if (cancelled) return;
        setUnmatchedCategories([]);
      });
    return () => {
      cancelled = true;
    };
  }, [projectTagLevelId, hasCoa, docIdsKey, iifTxnType]);

  const onDownload = () => {
    setLoading(true);
    const startedAt = Date.now();
    const invoiceCount = selectedDocumentIds?.length || 0;
    trackInvoiceExportAttempted({
      target: "iif",
      invoiceCount,
      fieldMapping: iifTxnType,
    });
    return downloadSpreadsheetHelper(
      job,
      l1,
      l2,
      "Invoice",
      "iif",
      {
        dateFormat: options[`docuclipper.prefs.dateFormat`] || "",
        iifTxnType,
      },
      selectedDocumentIds,
      selectedFieldNames,
      [],
      convertExternalFiltersToTableFilterConfig(externalFilters),
      externalFilterSortBy,
      externalFilterSortDirection,
      undefined,
      false
    )
      .then(() => {
        trackInvoiceExportSucceeded({
          target: "iif",
          invoiceCount,
          durationMs: Date.now() - startedAt,
        });
        if (onExportSucceeded) onExportSucceeded();
        setLoading(false);
      })
      .catch((err) => {
        trackInvoiceExportFailed({
          target: "iif",
          errorCode: err?.errorCode || err?.status,
          errorMessage: err?.message || String(err),
          durationMs: Date.now() - startedAt,
        });
        setLoading(false);
      });
  };

  return (
    <div>
      <Row>
        <Col className="my-2">
          <Form.Label className="font-weight-bold mb-1">
            Transaction type
            <PopoverHelp header="" id="iifTxnTypeHelp" placement="bottom">
              Choose <strong>Vendor Bill</strong> for invoices you received from
              suppliers (accounts payable). Choose <strong>Sales Invoice</strong>{" "}
              for invoices you sent to customers (accounts receivable).
            </PopoverHelp>
          </Form.Label>
          <div>
            <Form.Check
              type="radio"
              id="iifTxnTypeBill"
              name="iifTxnType"
              label="Vendor Bill, accounts payable (most common)"
              checked={iifTxnType === "BILL"}
              onChange={() => setIifTxnType("BILL")}
            />
            <Form.Check
              type="radio"
              id="iifTxnTypeInvoice"
              name="iifTxnType"
              label="Sales Invoice, accounts receivable"
              checked={iifTxnType === "INVOICE"}
              onChange={() => setIifTxnType("INVOICE")}
            />
          </div>
        </Col>
      </Row>

      {hasCoa ? (
        <Alert variant="info" className="small mb-3">
          <FontAwesomeIcon icon="check-circle" className="mr-2" />
          Categories will be validated against {coa!.length} accounts in your
          QuickBooks Desktop Chart of Accounts. Unmatched names will be flagged
          at the top of the IIF file.{" "}
          <a
            href={projectSettingsHref}
            target="_blank"
            rel="noopener noreferrer"
          >
            Manage Chart of Accounts
          </a>
        </Alert>
      ) : (
        <Alert variant="warning" className="small mb-3">
          <FontAwesomeIcon icon="exclamation-triangle" className="mr-2" />
          <strong>
            Categories must match your QuickBooks Desktop accounts exactly.
          </strong>{" "}
          Lines with unmatched categories fall back to{" "}
          {iifTxnType === "INVOICE" ? "Sales Income" : "Uncategorized Expenses"}.{" "}
          <a
            href={projectSettingsHref}
            target="_blank"
            rel="noopener noreferrer"
          >
            Upload your Chart of Accounts
          </a>{" "}
          so we can validate before download.{" "}
          <a href={KB_INVOICE_IIF} target="_blank" rel="noopener noreferrer">
            Learn more
          </a>
        </Alert>
      )}

      {unmatchedCategories.length > 0 && (
        <Alert variant="warning" className="small mb-3">
          <FontAwesomeIcon icon="exclamation-triangle" className="mr-2" />
          <strong>
            {unmatchedCategories.length} line categor
            {unmatchedCategories.length === 1 ? "y is" : "ies are"} not in your
            QuickBooks Desktop Chart of Accounts:
          </strong>
          <ul className="mb-1 mt-1 pl-4">
            {unmatchedCategories.slice(0, 10).map(c => (
              <li key={c}>{c}</li>
            ))}
          </ul>
          {unmatchedCategories.length > 10 && (
            <div className="mb-1">
              ...and {unmatchedCategories.length - 10} more.
            </div>
          )}
          Edit categories on your invoices to match QuickBooks Desktop account
          names, or{" "}
          <a
            href={projectSettingsHref}
            target="_blank"
            rel="noopener noreferrer"
          >
            add the missing names to your Chart of Accounts
          </a>
          . QuickBooks will reject any line whose category does not exist.
        </Alert>
      )}

      <Row className="justify-content-end mx-1">
        <LoadingTrackedButton
          isLoading={loading}
          className="btn-save-iif justify-content-center m-2"
          variant="primary"
          onClick={onDownload}
        >
          Download IIF
        </LoadingTrackedButton>
      </Row>
    </div>
  );
};

export default InvoiceIifOptions;
